import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { flushSync } from "react-dom";
import { useRole } from "../context/RoleContext";
import {
  getAdminArchitect,
  publishArchitectProfile,
  unpublishArchitectProfile,
  deactivateUser,
  reactivateUser,
  startImpersonation,
  approveVerification,
  rejectVerification,
} from "../lib/adminPortal";
import { Toaster, toast } from "sonner";
import { ChevronRight, ExternalLink, Eye, AlertTriangle, Check, X } from "lucide-react";
import { assetUrl, formatTypology } from "../lib/format";
import BackBar from "../components/BackBar";

export default function AdminArchitectDetail() {
  const { architectId } = useParams();
  const { user, beginImpersonation } = useRole();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [action, setAction] = useState(null); // unpublish | publish | deactivate | reactivate | impersonate | reject_verify
  const [reason, setReason] = useState("");

  const refresh = async () => {
    try {
      setA(await getAdminArchitect(architectId, user.id));
    } catch (e) {
      setErr(e);
    }
  };
  useEffect(() => {
    if (!user?.id) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, architectId]);

  if (err) return <p style={{ color: "#A23B2E" }} data-testid="admin-arch-error">Could not load this architect.</p>;
  if (!a) return <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="admin-arch-loading">Loading…</div>;

  const isDeactivated = a.user?.account_status === "deactivated";
  const pending = a.pending_verification || {};
  const hasPending =
    pending && Object.keys(pending).some(
      (k) => k !== "requested_at" && k !== "note" && pending[k] != null,
    );

  const onSubmit = async () => {
    if ((action === "unpublish" || action === "deactivate" || action === "impersonate" || action === "reject_verify") && reason.trim().length < 20) return;
    setBusy(true);
    try {
      if (action === "unpublish") await unpublishArchitectProfile(architectId, user.id, reason.trim());
      else if (action === "publish") await publishArchitectProfile(architectId, user.id, reason.trim());
      else if (action === "deactivate" && a.user?.id) await deactivateUser(a.user.id, user.id, reason.trim());
      else if (action === "reactivate" && a.user?.id) await reactivateUser(a.user.id, user.id, reason.trim());
      else if (action === "impersonate" && a.user?.id) {
        const { target } = await startImpersonation(a.user.id, user.id, reason.trim());
        // Commit the impersonation state synchronously so the subsequent
        // navigate is evaluated with the new (architect) role under
        // RequireRole/ArchitectArea — otherwise the route guard sees the
        // admin role and bounces us to /sign-in.
        flushSync(() => beginImpersonation(target, reason.trim()));
        toast.success(`Viewing as ${target.name}.`);
        const home = target.role === "owner_builder" ? "/owner"
          : target.role === "architect" ? "/architect"
          : target.role === "city_staff" ? "/city" : "/";
        navigate(home, { replace: true });
        return;
      }
      else if (action === "approve_verify") {
        await approveVerification(architectId, user.id, reason.trim() || undefined);
        toast.success("Verification approved.");
      }
      else if (action === "reject_verify") {
        await rejectVerification(architectId, user.id, reason.trim());
        toast.success("Verification rejected.");
      }
      setAction(null);
      setReason("");
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Action failed.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="admin-arch-detail">
      <Toaster position="top-right" richColors closeButton />
      <BackBar
        parent={{ to: "/admin/architects", label: "Architects" }}
        trail={[
          { label: "Admin", to: "/admin" },
          { label: "Architects", to: "/admin/architects" },
          { label: a.firm || a.name },
        ]}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            {a.firm || a.name}
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
            }}
          >
            {a.name}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {a.verified_badge && (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "#4F6149",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: "#4F6149" }} />
              Verified
            </span>
          )}
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 999,
              background: a.profile_published ? "rgba(177,78,47,0.10)" : "rgba(154,160,192,0.20)",
              color: a.profile_published ? "#8F3C23" : "#6C6B85",
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
            data-testid="arch-profile-state"
          >
            {a.profile_published ? "Profile Published" : "Profile Unpublished"}
          </span>
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 999,
              background: isDeactivated ? "rgba(154,160,192,0.30)" : "rgba(79,97,73,0.10)",
              color: isDeactivated ? "#6C6B85" : "#4F6149",
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
            data-testid="arch-account-state"
          >
            {isDeactivated ? "Account Deactivated" : "Account Active"}
          </span>
        </div>
      </div>

      <div className="mt-7 grid lg:grid-cols-[3fr_2fr] gap-7">
        <div className="space-y-6">
          {/* Pending verification banner (architect requested an edit) */}
          {hasPending && (
            <PendingVerification
              pending={pending}
              current={{
                years_practising: a.years_practising,
                license_no: a.license_no,
              }}
              onApprove={() => { setAction("approve_verify"); setReason(""); }}
              onReject={() => { setAction("reject_verify"); setReason(""); }}
            />
          )}

          {/* Hero: firm photo + photo + tagline */}
          <ProfileHero a={a} />

          {/* Identity & credentials */}
          <div className="wn-card" style={{ padding: 18 }} data-testid="arch-identity">
            <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
              Identity & at-a-glance.
            </h2>
            <dl
              className="mt-3 grid grid-cols-2 gap-y-2 gap-x-6 tabular-nums"
              style={{ fontSize: 13, color: "#272636" }}
            >
              <K label="Firm" v={a.firm} />
              <K label="Architect name" v={a.name} />
              <K label="License" v={a.license_no} />
              <K label="Year founded" v={a.years_founded} />
              <K label="Team size" v={a.team_size} />
              <K label="Years practising" v={a.years_practising ? `${a.years_practising}+` : ""} />
              <K
                label="Contact email"
                v={a.user?.email || a.email || "—"}
              />
              <K
                label="Contact phone"
                v={a.user?.phone || a.phone || "—"}
              />
              <K label="Service areas" v={(a.service_areas || []).join(", ")} />
              <K label="Demographic flags" v={(a.demographic_flags || []).join(", ") || "—"} />
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/architects/${a.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="wn-btn wn-btn-tertiary"
                style={{ paddingLeft: 0 }}
                data-testid="arch-preview-public-link"
              >
                Preview public profile <ExternalLink size={12} />
              </Link>
              {a.application_id && (
                <Link
                  to={`/admin/applications/${a.application_id}`}
                  className="wn-btn wn-btn-tertiary"
                  style={{ paddingLeft: 0 }}
                  data-testid="arch-application-link"
                >
                  View originating application <ChevronRight size={12} />
                </Link>
              )}
            </div>
          </div>

          {/* Practice writing */}
          <ProfileWriting a={a} />

          {/* Specialisations / lifestyles */}
          <ProfileFocus a={a} />

          {/* Credentials / Education / Awards */}
          <ProfileCredentials a={a} />

          {/* Portfolio designs */}
          <DesignsListCard a={a} />

          {/* Projects */}
          <ProjectsListCard a={a} />

          {/* Project highlights */}
          <ProfileHighlights a={a} />

          {/* Deactivation reason (if any) */}
          {a.unpublish_reason && (
            <div
              className="wn-card"
              style={{
                padding: "14px 18px",
                borderLeft: "3px solid #B98A3C",
                background: "rgba(185,138,60,0.07)",
              }}
              data-testid="arch-unpublish-reason"
            >
              <div className="wn-label" style={{ color: "#8a651f" }}>
                Last unpublish reason
              </div>
              <p style={{ color: "#272636", fontSize: 13, marginTop: 4, lineHeight: 1.6 }}>
                {a.unpublish_reason}
              </p>
            </div>
          )}
        </div>

        <aside data-testid="arch-actions">
          <div className="wn-card" style={{ padding: 18 }}>
            <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
              Management Actions.
            </h2>

            {!action && (
              <div className="mt-3 space-y-2">
                <button
                  className="wn-btn wn-btn-primary w-full inline-flex items-center justify-center"
                  style={{ gap: 6 }}
                  onClick={() => { setAction("impersonate"); setReason(""); }}
                  disabled={isDeactivated || !a.user?.id}
                  data-testid="arch-impersonate-open"
                  title={isDeactivated ? "Reactivate the account first" : "View the platform through this architect's eyes"}
                >
                  <Eye size={14} /> View as {a.name || "this architect"}
                </button>
                <Link
                  to={`/admin/architects/${a.id}/edit`}
                  className="wn-btn wn-btn-outline w-full"
                  style={{ justifyContent: "center" }}
                  data-testid="arch-edit-open"
                >
                  Edit profile on their behalf
                </Link>
                {a.profile_published ? (
                  <button
                    className="wn-btn wn-btn-outline w-full"
                    onClick={() => setAction("unpublish")}
                    data-testid="arch-unpublish-open"
                  >
                    Unpublish public profile
                  </button>
                ) : (
                  <button
                    className="wn-btn wn-btn-outline w-full"
                    onClick={() => setAction("publish")}
                    data-testid="arch-publish-open"
                  >
                    Re-publish public profile
                  </button>
                )}
                {!isDeactivated && (
                  <button
                    className="wn-btn wn-btn-tertiary w-full"
                    style={{ color: "#A23B2E", justifyContent: "center" }}
                    onClick={() => setAction("deactivate")}
                    data-testid="arch-deactivate-open"
                  >
                    Deactivate Account
                  </button>
                )}
                {isDeactivated && (
                  <button
                    className="wn-btn wn-btn-primary w-full"
                    onClick={() => setAction("reactivate")}
                    data-testid="arch-reactivate-open"
                  >
                    Reactivate Account
                  </button>
                )}
              </div>
            )}

            {action && (
              <div className="mt-3" data-testid={`arch-action-form-${action}`}>
                <p style={{ color: "#6C6B85", fontSize: 12.5 }}>
                  {action === "unpublish" && "Removes the profile from the public listing. The architect can still sign in."}
                  {action === "publish" && "Returns the profile to the public listing."}
                  {action === "deactivate" && "The architect cannot sign in or act on the platform. Reversible."}
                  {action === "reactivate" && "Restores the architect's ability to sign in and act."}
                  {action === "impersonate" && "You'll step into this architect's portal. All actions are audit-logged and the architect is notified by email."}
                  {action === "approve_verify" && "Promote the architect's pending years_practising / license update to the live record."}
                  {action === "reject_verify" && "Reject the architect's pending update. They'll be notified with your note and can resubmit."}
                </p>
                <label className="wn-label mt-3 block">
                  {action === "publish" || action === "reactivate" || action === "approve_verify" ? "Note (optional)" : "Reason (≥ 20 chars)"}
                </label>
                <textarea
                  className="wn-input"
                  style={{ minHeight: 90, resize: "vertical" }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid={`arch-action-reason-${action}`}
                />
                {(action === "unpublish" || action === "deactivate" || action === "impersonate" || action === "reject_verify") && (
                  <div
                    className="tabular-nums"
                    style={{ fontSize: 11, color: reason.trim().length < 20 ? "#A23B2E" : "#6C6B85", marginTop: 2 }}
                  >
                    {reason.trim().length} / 20 characters
                  </div>
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    className="wn-btn wn-btn-tertiary"
                    onClick={() => { setAction(null); setReason(""); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="wn-btn wn-btn-primary"
                    onClick={onSubmit}
                    disabled={
                      busy ||
                      ((action === "unpublish" || action === "deactivate" || action === "impersonate" || action === "reject_verify") && reason.trim().length < 20)
                    }
                    data-testid={`arch-action-confirm-${action}`}
                  >
                    {busy ? "Working…" : (action === "impersonate" ? "Start View-as session" : "Confirm")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

function K({ label, v }) {
  return (
    <>
      <dt style={{ color: "#6C6B85" }}>{label}</dt>
      <dd style={{ fontWeight: 600, color: "#272636" }}>{v == null || v === "" ? "—" : v}</dd>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────
   Read-only sub-cards — give the admin parity with the public
   profile, plus admin-only fields (designs/projects counts, contact
   info, originating application).
   ──────────────────────────────────────────────────────────────── */
function ProfileHero({ a }) {
  return (
    <div
      className="wn-card overflow-hidden"
      style={{ padding: 0 }}
      data-testid="arch-hero"
    >
      <div
        style={{
          width: "100%",
          aspectRatio: "16 / 7",
          backgroundColor: "#EEEDF3",
          backgroundImage: a.firm_photo
            ? `url(${assetUrl(a.firm_photo)})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderBottom: "1px solid #D9D8E4",
        }}
        data-testid="arch-hero-firm-photo"
      />
      <div style={{ padding: "16px 18px" }} className="flex items-center gap-4">
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 6,
            border: "1px solid #D9D8E4",
            background: a.photo
              ? `url(${assetUrl(a.photo)}) center/cover no-repeat`
              : "#EEEDF3",
            flexShrink: 0,
          }}
          data-testid="arch-hero-headshot"
        />
        <div style={{ minWidth: 0 }}>
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Profile preview
          </div>
          {a.firm_tagline && (
            <div
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 15,
                color: "#272636",
                lineHeight: 1.45,
                marginTop: 2,
              }}
            >
              {a.firm_tagline}
            </div>
          )}
          {!a.firm_photo && (
            <div
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#A23B2E",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              No firm hero photo on file.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileWriting({ a }) {
  if (!a.practice_statement && !a.bio && !a.philosophy_quote) return null;
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-writing">
      <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
        Practice writing.
      </h2>
      {a.practice_statement && (
        <div className="mt-3">
          <div className="wn-label">Practice statement</div>
          <p
            className="mt-1"
            style={{ color: "#272636", fontSize: 14, lineHeight: 1.65, whiteSpace: "pre-wrap" }}
          >
            {a.practice_statement}
          </p>
        </div>
      )}
      {a.bio && (
        <div className="mt-4">
          <div className="wn-label">Biography</div>
          <p
            className="mt-1"
            style={{ color: "#272636", fontSize: 14, lineHeight: 1.6 }}
          >
            {a.bio}
          </p>
        </div>
      )}
      {a.philosophy_quote && (
        <blockquote
          className="mt-4"
          style={{
            margin: 0,
            padding: "8px 0 8px 16px",
            borderLeft: "3px solid #B14E2F",
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 17,
            lineHeight: 1.45,
            color: "#1B1F4B",
          }}
          data-testid="arch-philosophy-quote"
        >
          “{a.philosophy_quote}”
        </blockquote>
      )}
    </div>
  );
}

function ProfileFocus({ a }) {
  const specs = a.specialisation_labels
    || (a.specialisations || []).map((s) => s.replace(/_/g, " "));
  const lifestyles = a.lifestyle_focuses || [];
  if (specs.length === 0 && lifestyles.length === 0) return null;
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-focus">
      <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
        What they do · who they serve.
      </h2>
      {specs.length > 0 && (
        <div className="mt-3">
          <div className="wn-label">Specialisations</div>
          <ChipList items={specs} testidPrefix="arch-spec" />
        </div>
      )}
      {lifestyles.length > 0 && (
        <div className="mt-4">
          <div className="wn-label">Lifestyle focuses</div>
          <ChipList items={lifestyles} testidPrefix="arch-lifestyle" />
        </div>
      )}
    </div>
  );
}

function ChipList({ items, testidPrefix }) {
  return (
    <div className="flex flex-wrap mt-2" style={{ gap: 6 }}>
      {items.map((it, i) => (
        <span
          key={`${it}-${i}`}
          data-testid={`${testidPrefix}-${i}`}
          style={{
            padding: "4px 11px",
            borderRadius: 999,
            border: "1px solid #D9D8E4",
            color: "#272636",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {it}
        </span>
      ))}
    </div>
  );
}

function ProfileCredentials({ a }) {
  const c = a.credentials || [];
  const e = a.education || [];
  const w = a.awards || [];
  if (c.length === 0 && e.length === 0 && w.length === 0) return null;
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-credentials">
      <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
        Credentials & recognition.
      </h2>
      <div className="mt-3 grid md:grid-cols-3 gap-5">
        <CredColumn title="Credentials" rows={c} fmt={(r) => ({
          h: [r.abbr || r.credential, r.year].filter(Boolean).join(" · "),
          sub: r.name || r.description,
        })} />
        <CredColumn title="Education" rows={e} fmt={(r) => ({
          h: [r.degree, r.year].filter(Boolean).join(" · "),
          sub: r.institution,
        })} />
        <CredColumn title="Recognition" rows={w} fmt={(r) => ({
          h: [r.name, r.year].filter(Boolean).join(" · "),
          sub: [r.body, r.description].filter(Boolean).join(" — "),
        })} />
      </div>
    </div>
  );
}

function CredColumn({ title, rows, fmt }) {
  return (
    <div>
      <div className="wn-label" style={{ color: "#8F3C23" }}>{title}</div>
      <div className="mt-2 space-y-3">
        {rows.length === 0 && (
          <div style={{ color: "#6C6B85", fontSize: 12, fontStyle: "italic" }}>
            None on file.
          </div>
        )}
        {rows.map((r, i) => {
          const f = fmt(r);
          return (
            <div key={i} style={{ fontSize: 12.5 }}>
              <div
                style={{
                  color: "#272636",
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  lineHeight: 1.4,
                }}
                className="tabular-nums"
              >
                {f.h || "—"}
              </div>
              {f.sub && (
                <div style={{ color: "#6C6B85", marginTop: 2, lineHeight: 1.45 }}>
                  {f.sub}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DesignsListCard({ a }) {
  const designs = a.designs_list || a.portfolio_designs || [];
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-designs">
      <div className="flex items-center justify-between gap-3">
        <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
          Designs in the Registry.
        </h2>
        <span className="wn-label tabular-nums" style={{ color: "#6C6B85" }}>
          {designs.length} total
        </span>
      </div>
      {designs.length === 0 ? (
        <div className="mt-3" style={{ color: "#6C6B85", fontSize: 12.5, fontStyle: "italic" }}>
          No designs in the Registry yet.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {designs.map((d) => (
            <Link
              key={d.id}
              to={`/marketplace/${d.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="wn-card flex items-center gap-3"
              style={{ padding: "10px 12px", textDecoration: "none" }}
              data-testid={`arch-design-${d.id}`}
            >
              <div
                style={{
                  width: 56,
                  height: 42,
                  borderRadius: 4,
                  border: "1px solid #ECEBF1",
                  background: d.exterior_photo
                    ? `url(${assetUrl(d.exterior_photo)}) center/cover no-repeat`
                    : "#EEEDF3",
                  flexShrink: 0,
                }}
              />
              <div className="min-w-0 flex-1">
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    color: "#1B1F4B",
                    fontSize: 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.name || d.id}
                </div>
                <div
                  className="tabular-nums"
                  style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 1 }}
                >
                  {d.typology ? formatTypology(d.typology) : "—"}
                  {d.beds != null && d.baths != null && ` · ${d.beds} BR / ${d.baths} BA`}
                  {d.sqft != null && ` · ${d.sqft} sqft`}
                  {d.year_designed && ` · ${d.year_designed}`}
                </div>
              </div>
              <DesignStatusPill status={d.status} />
              <ExternalLink size={12} color="#6C6B85" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function DesignStatusPill({ status }) {
  const map = {
    "Approved":      { bg: "rgba(79,97,73,0.10)", fg: "#4F6149" },
    "In Review":     { bg: "rgba(91,97,150,0.12)", fg: "#5B6196" },
    "Needs Revision":{ bg: "rgba(185,138,60,0.14)", fg: "#8a651f" },
    "Draft":         { bg: "#ECEBF1", fg: "#6C6B85" },
    "Rejected":      { bg: "rgba(162,59,46,0.10)", fg: "#A23B2E" },
  };
  const s = map[status] || map["Draft"];
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {status || "—"}
    </span>
  );
}

function ProjectsListCard({ a }) {
  const projects = a.projects_list || [];
  const active = projects.filter((p) => p.is_active).length;
  const completed = projects.length - active;
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-projects">
      <div className="flex items-center justify-between gap-3">
        <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
          Projects under construction & complete.
        </h2>
        <span className="wn-label tabular-nums" style={{ color: "#6C6B85" }}>
          {active} active · {completed} complete
        </span>
      </div>
      {projects.length === 0 ? (
        <div className="mt-3" style={{ color: "#6C6B85", fontSize: 12.5, fontStyle: "italic" }}>
          No projects on file.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {projects.map((p) => (
            <div
              key={p.id}
              className="wn-card"
              style={{ padding: "10px 12px" }}
              data-testid={`arch-project-${p.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div
                    style={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 600,
                      color: "#1B1F4B",
                      fontSize: 14,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.parcel_address || p.id}
                  </div>
                  <div
                    style={{
                      color: "#6C6B85",
                      fontSize: 11.5,
                      marginTop: 1,
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                    }}
                  >
                    {p.design_name || "—"} · for {p.owner_name || "—"}
                  </div>
                </div>
                <span
                  style={{
                    padding: "2px 9px",
                    borderRadius: 999,
                    background: p.is_active ? "rgba(177,78,47,0.10)" : "rgba(79,97,73,0.12)",
                    color: p.is_active ? "#8F3C23" : "#4F6149",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.current_stage || (p.is_active ? "Active" : "Complete")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PendingVerification({ pending, current, onApprove, onReject }) {
  const requested = (key) => pending[key];
  const same = (key) => requested(key) === current[key];
  const rows = ["years_practising", "license_no"]
    .filter((k) => requested(k) != null && !same(k));
  return (
    <div
      className="wn-card"
      style={{
        padding: 16,
        borderLeft: "3px solid #B14E2F",
        background: "rgba(177,78,47,0.05)",
      }}
      data-testid="arch-pending-verification"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} color="#8F3C23" style={{ flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1">
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Verification request — needs your review
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              color: "#1B1F4B",
              fontSize: 16,
            }}
          >
            The architect has requested updates to verification-gated fields.
          </div>
          <dl
            className="mt-3 grid grid-cols-[180px_1fr_1fr] gap-y-1 gap-x-3 tabular-nums"
            style={{ fontSize: 13, color: "#272636" }}
          >
            <dt style={{ color: "#6C6B85" }}>&nbsp;</dt>
            <dt style={{ color: "#6C6B85" }}>Current</dt>
            <dt style={{ color: "#8F3C23", fontWeight: 700 }}>Proposed</dt>
            {rows.map((k) => (
              <React.Fragment key={k}>
                <dt style={{ color: "#6C6B85" }}>
                  {k === "license_no" ? "License number" : "Years practising"}
                </dt>
                <dd>{current[k] == null || current[k] === "" ? "—" : String(current[k])}</dd>
                <dd style={{ color: "#8F3C23", fontWeight: 700 }}>
                  {requested(k) == null ? "—" : String(requested(k))}
                </dd>
              </React.Fragment>
            ))}
          </dl>
          {pending.note && (
            <div
              className="mt-3"
              style={{
                color: "#272636",
                fontSize: 13,
                lineHeight: 1.5,
                borderLeft: "2px solid #D9D8E4",
                paddingLeft: 10,
                fontStyle: "italic",
              }}
            >
              "{pending.note}"
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="wn-btn wn-btn-primary inline-flex items-center"
              style={{ gap: 6 }}
              onClick={onApprove}
              data-testid="arch-verify-approve"
            >
              <Check size={13} /> Approve update
            </button>
            <button
              type="button"
              className="wn-btn wn-btn-outline inline-flex items-center"
              style={{ gap: 6, color: "#A23B2E", borderColor: "#A23B2E" }}
              onClick={onReject}
              data-testid="arch-verify-reject"
            >
              <X size={13} /> Reject…
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileDesigns({ a }) {
  const designs = a.portfolio_designs || [];
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-designs">
      <div className="flex items-center justify-between gap-3">
        <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
          Designs in the Registry.
        </h2>
        <span className="wn-label tabular-nums" style={{ color: "#6C6B85" }}>
          {a.design_count != null ? a.design_count : designs.length} total
        </span>
      </div>
      {designs.length === 0 ? (
        <div className="mt-3" style={{ color: "#6C6B85", fontSize: 12.5, fontStyle: "italic" }}>
          No published designs yet.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {designs.slice(0, 6).map((d) => (
            <Link
              key={d.id}
              to={`/marketplace/${d.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="wn-card flex items-center gap-3"
              style={{ padding: "10px 12px", textDecoration: "none" }}
              data-testid={`arch-design-${d.id}`}
            >
              <div
                style={{
                  width: 56,
                  height: 42,
                  borderRadius: 4,
                  border: "1px solid #ECEBF1",
                  background: d.exterior_photo
                    ? `url(${assetUrl(d.exterior_photo)}) center/cover no-repeat`
                    : "#EEEDF3",
                  flexShrink: 0,
                }}
              />
              <div className="min-w-0 flex-1">
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    color: "#1B1F4B",
                    fontSize: 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.name || d.id}
                </div>
                <div
                  className="tabular-nums"
                  style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 1 }}
                >
                  {d.beds || "—"} BR · {d.baths || "—"} BA · {d.sqft || "—"} sqft
                </div>
              </div>
              <ExternalLink size={12} color="#6C6B85" />
            </Link>
          ))}
          {designs.length > 6 && (
            <div style={{ color: "#6C6B85", fontSize: 12 }}>
              + {designs.length - 6} more
            </div>
          )}
        </div>
      )}
      <div
        className="mt-3 tabular-nums"
        style={{ color: "#6C6B85", fontSize: 12 }}
      >
        Active projects · <span style={{ color: "#1B1F4B", fontWeight: 700 }}>{a.active_project_count ?? 0}</span>
      </div>
    </div>
  );
}

function ProfileHighlights({ a }) {
  const items = a.project_highlights || [];
  if (items.length === 0) return null;
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="arch-highlights">
      <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
        Completed-project highlights.
      </h2>
      <div className="mt-3 space-y-3">
        {items.map((h, i) => (
          <div
            key={i}
            className="wn-card"
            style={{ padding: "10px 12px" }}
            data-testid={`arch-highlight-${i}`}
          >
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 72,
                  height: 56,
                  borderRadius: 4,
                  border: "1px solid #ECEBF1",
                  background: h.image_ref
                    ? `url(${assetUrl(h.image_ref)}) center/cover no-repeat`
                    : "#EEEDF3",
                  flexShrink: 0,
                }}
              />
              <div className="min-w-0 flex-1">
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#1B1F4B",
                  }}
                >
                  {h.project_name || h.title || "Untitled"}
                  {h.completion_year && (
                    <span
                      className="tabular-nums"
                      style={{
                        marginLeft: 8,
                        fontFamily: '"Newsreader", Georgia, serif',
                        fontStyle: "italic",
                        fontWeight: 400,
                        color: "#6C6B85",
                        fontSize: 12,
                      }}
                    >
                      {h.completion_year}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    color: "#6C6B85",
                    fontSize: 11.5,
                    marginTop: 2,
                    fontStyle: "italic",
                    fontFamily: '"Newsreader", Georgia, serif',
                  }}
                >
                  {[h.neighborhood || h.location, h.lot_dimensions, h.client_type]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </div>
                {h.narrative && (
                  <p
                    style={{
                      color: "#272636",
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      marginTop: 4,
                    }}
                  >
                    {h.narrative}
                  </p>
                )}
                {h.built_from_design_id && (
                  <Link
                    to={`/marketplace/${h.built_from_design_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      marginTop: 6,
                      color: "#8F3C23",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      textDecoration: "none",
                    }}
                  >
                    Built from {h.built_from_design_name || h.built_from_design_id} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 8, fontStyle: "italic" }}>
        Highlights are curated by platform staff; admin-side editing is a planned
        future enhancement.
      </p>
    </div>
  );
}
